import { IconButton, Sheet, Badge, Typography, Box, Tooltip } from "@mui/joy";
import { AnimatePresence } from 'framer-motion';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import ContactSupportRoundedIcon from '@mui/icons-material/ContactSupportRounded';

import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { LayoutContext } from "../../context";
import { openSidebar } from "../../utils";
import { StoriesRow, StoriesInline } from "./StoriesBar";
import DesktopStoriesWidget from "./DesktopStoriesWidget";

// Контент скроллится во внутреннем Box — ищем ближайшего скроллящегося родителя.
function getScrollParent(node) {
    let el = node?.parentElement;
    while (el) {
        const oy = getComputedStyle(el).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight) return el;
        el = el.parentElement;
    }
    return window;
}

const HEADER_H = 55;

export default function HeaderLK() {
    const {
        notifUnread, setNotifPanelOpen,
        setSidebarOpen, account, setModalKonsolConnect, setModalTicketSupport,
        storiesBar, setNewsStories, loadStoriesBar, newsStories,
        tokenEmployee, subscribeUserToPush, checkNotificationStatus, setModalIOSNotifHelp,
    } = useContext(LayoutContext);

    const items = useMemo(() => storiesBar?.items || [], [storiesBar]);
    const storiesOpen = !!newsStories?.open;
    const [rowVisible, setRowVisible] = useState(true);   // видна ли лента (мы у верха)
    const rowRef = useRef(null);
    const scrollElRef = useRef(null);

    // Грузим при монтировании и при каждом закрытии плеера (обновить кольца «просмотрено»).
    useEffect(() => { if (!storiesOpen) loadStoriesBar?.(); }, [storiesOpen, loadStoriesBar]);

    // Следим за лентой через IntersectionObserver: уехала под шапку → показываем стопку.
    // Высота шапки постоянна → обратной связи (скачков scrollTop) нет → мерцание уходит.
    useEffect(() => {
        if (!items.length || !rowRef.current) { setRowVisible(true); return; }
        const scroller = getScrollParent(rowRef.current);
        scrollElRef.current = scroller;
        const root = scroller === window ? null : scroller;
        const io = new IntersectionObserver(
            ([entry]) => setRowVisible(entry.isIntersecting),
            { root, rootMargin: `-${HEADER_H + 4}px 0px 0px 0px`, threshold: 0 },
        );
        io.observe(rowRef.current);
        return () => io.disconnect();
    }, [items.length]);

    const openAt = (idx) => setNewsStories({ open: true, items, startIndex: idx, preview: false });
    const scrollTop = () => {
        const s = scrollElRef.current;
        if (s === window) window.scrollTo({ top: 0, behavior: 'smooth' });
        else if (s) s.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const hasStories = items.length > 0;
    const showInline = hasStories && !rowVisible;

    // Кнопка PUSH — только если уведомления НЕ подключены.
    const notifGranted = checkNotificationStatus()?.permission === 'granted';
    const showPushBtn = !!tokenEmployee && !notifGranted;

    const handleEnableNotifications = async () => {
        if (!tokenEmployee) return;
        const st = checkNotificationStatus();
        if (!st) { alert('Ваш браузер не поддерживает уведомления. Попробуйте добавить сайт на экран «Домой».'); return; }
        if (st.permission === 'granted') return;
        if (st.permission === 'denied') {
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
            const isAndroid = /Android/i.test(navigator.userAgent);
            if (isIOS) { setModalIOSNotifHelp(true); return; }
            if (isAndroid) { alert('Уведомления отключены. Включите: 🔒 в адресной строке → Разрешения → Уведомления → Разрешить.'); return; }
            alert('Уведомления отключены. Включите: 🔒 слева от адреса → Настройки сайта → Уведомления → Разрешить.');
            return;
        }
        const result = await subscribeUserToPush(tokenEmployee);
        switch (result?.type) {
            case 'permission_denied': alert('Вы отклонили запрос на уведомления.'); break;
            case 'permission_denied_permanent': alert('Вы ранее отключили уведомления. Включите их в настройках браузера.'); break;
            case 'ios_need_install': alert('Для уведомлений на iPhone: «Поделиться» → «На экран Домой» → откройте с домашнего экрана.'); break;
            case 'not_supported': alert('Ваш браузер не поддерживает push-уведомления.'); break;
            default: break;
        }
    };

    return (
        <>
            {/* ── ЛИПКАЯ ШАПКА ПК: сторис + PUSH + колокол (только md+) ── */}
            <Sheet
                component="header"
                sx={{
                    position: 'sticky', top: 0, zIndex: 98,
                    boxShadow: 'sm', bgcolor: 'background.body',
                    display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1.5,
                    px: 3, minHeight: HEADER_H,
                    borderBottom: '1px solid', borderColor: 'background.level1',
                }}
            >
                {/* Сторис — компактная пилюля слева (разворачивается по клику) */}
                <DesktopStoriesWidget />

                <Box sx={{ flex: 1 }} />

                {/* Тех. поддержка */}
                <Tooltip title="Тех. поддержка" variant="soft" size="sm">
                    <IconButton
                        variant="soft" color="success" size="sm"
                        onClick={() => setModalTicketSupport(true)}
                    >
                        <ContactSupportRoundedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>

                {/* Кнопка включения PUSH — только если не подключены */}
                {showPushBtn && (
                    <Tooltip title="Включить push-уведомления" variant="soft" size="sm">
                        <IconButton
                            variant="soft" color="danger" size="sm"
                            onClick={handleEnableNotifications}
                            sx={{
                                animation: 'pulsePush 1.6s ease-in-out infinite',
                                '@keyframes pulsePush': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.55 } },
                            }}
                        >
                            <NotificationsActiveRoundedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                )}

                {/* Колокол уведомлений */}
                <Badge
                    badgeContent={notifUnread} color="danger" max={99} size="sm"
                    invisible={!notifUnread} badgeInset="2px 6px 0 0"
                    slotProps={{ badge: { sx: { fontSize: '9px', fontWeight: 700, minWidth: 16, height: 16 } } }}
                >
                    <IconButton variant="plain" onClick={() => setNotifPanelOpen(true)} title="Уведомления">
                        <NotificationsRoundedIcon />
                    </IconButton>
                </Badge>
            </Sheet>

            {/* СТИКИ-ШАПКА (моб.): постоянная высота — только верхняя строка */}
            <Sheet
                component="header"
                sx={{
                    position: 'sticky', top: 0, zIndex: 98,
                    boxShadow: 'sm', bgcolor: 'background.body',
                    display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1,
                    pl: 1.5, pr: 2, minHeight: HEADER_H,
                    borderBottom: '1px solid', borderColor: 'background.level1',
                }}
            >
                <IconButton onClick={() => { openSidebar(); setSidebarOpen(true); }} variant="outlined" color="neutral" size="sm">
                    <MenuRoundedIcon />
                </IconButton>

                <Typography level="title-md" sx={{ fontWeight: 800, letterSpacing: 1.5, color: 'primary.600', flex: 1, ml: 0.5 }}>
                    PLANIUM
                </Typography>

                {/* Свёрнутые сторис — в строке, слева от кнопок */}
                <AnimatePresence initial={false}>
                    {showInline && <StoriesInline items={items} onOpen={openAt} onExpand={scrollTop} />}
                </AnimatePresence>

                {/* Тех. поддержка */}
                <IconButton
                    variant="soft" color="success" size="sm"
                    onClick={() => setModalTicketSupport(true)}
                    title="Тех. поддержка"
                >
                    <ContactSupportRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>

                {/* Кнопка включения PUSH — только если не подключены */}
                {showPushBtn && (
                    <Tooltip title="Включить push-уведомления" variant="soft" size="sm">
                        <IconButton
                            variant="soft" color="danger" size="sm"
                            onClick={handleEnableNotifications}
                            sx={{
                                animation: 'pulsePush 1.6s ease-in-out infinite',
                                '@keyframes pulsePush': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.55 } },
                            }}
                        >
                            <NotificationsActiveRoundedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                )}

                {/* Колокол — справа */}
                <Badge
                    badgeContent={notifUnread} color="danger" max={99} size="sm"
                    invisible={!notifUnread} badgeInset="2px 6px 0 0"
                    slotProps={{ badge: { sx: { fontSize: '9px', fontWeight: 700, minWidth: 16, height: 16 } } }}
                >
                    <IconButton variant="plain" onClick={() => setNotifPanelOpen(true)} title="Уведомления">
                        <NotificationsRoundedIcon />
                    </IconButton>
                </Badge>
            </Sheet>

            {/* ЛЕНТА СТОРИС: обычный блок под шапкой — уезжает при скролле сам */}
            {hasStories && (
                <Box ref={rowRef} sx={{ display: { xs: 'block', md: 'none' }, bgcolor: 'background.body' }}>
                    <StoriesRow items={items} onOpen={openAt} />
                </Box>
            )}

            {/* ── Баннер Консоль.HRM ── */}
            {(account?.position?.includes('surveyor') || account?.position?.includes('draftsman')) && !account?.id_contractor_konsol && (
                <Sheet
                    onClick={() => setModalKonsolConnect(true)}
                    sx={{
                        display: { xs: 'flex', md: 'none' },
                        alignItems: 'center', justifyContent: 'center', gap: 1,
                        px: 2, py: 0.8, cursor: 'pointer',
                        bgcolor: !account?.id_invite_konsol ? 'danger.500' : 'warning.300',
                        color: !account?.id_invite_konsol ? '#fff' : 'warning.800',
                        transition: 'opacity .2s', '&:active': { opacity: 0.8 },
                    }}
                >
                    {!account?.id_invite_konsol
                        ? <AccountBalanceWalletRoundedIcon sx={{ fontSize: 16 }} />
                        : <HourglassEmptyRoundedIcon sx={{ fontSize: 16 }} />}
                    <Typography level="body-xs" fontWeight={600} sx={{ color: 'inherit' }}>
                        {!account?.id_invite_konsol
                            ? 'Подключите Консоль.HRM для получения выплат'
                            : 'Документы на проверке — нажмите, чтобы подтвердить'}
                    </Typography>
                </Sheet>
            )}
        </>
    );
}